import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { Action, Getter, Mutation } from 'vuex-class';
import { RESET_STORE } from '@/types/store.types';
import { StoreNamespace } from '@/globals/StoreNamespaceGlobal';
import {
  AUTH_LOADING_MODE,
  LAST_SSO_USED_TOKEN,
  OIDC_ID,
  OIDC_SSO_LOGIN,
  OIDC_USER_MANAGER,
  SELECT_AUTH_METHOD_ERRORS,
  SET_AUTH_LOADING_MODE,
  SET_OIDC_ID,
  SET_OIDC_USER_MANAGER,
  SET_SELECT_AUTH_METHOD_ERRORS,
  SSO_LOGIN,
} from '@/types/authentication.types';
import { RecaptchaNamespace } from '@/globals/recaptcha-namespace.global';
import { MUST_PERFORM_DATA_CLEANSING } from '@/types/lobby.types';
import PerformDataCleansingModal from '@/components/modals/PerformDataCleansing.modal.vue';
import { User } from 'oidc-client-ts';
import { OidcLogin } from '@/globals/oidc-login';
import { OidcSsoRequest } from '@/models/request/OidcSsoRequest';
import { OidcUserManager } from '@/models/OidcUserManager.model';
import { OPEN_TERMS_AND_PRIVACY_MODAL, SET_OPEN_TERMS_AND_PRIVACY } from '@/types/system.types';

@Component({
  components: {
    PerformDataCleansingModal,
  },
})
export default class SelectAuthMethodComponent extends Vue {

  @Prop(String) public gov!: string;
  @Prop(String) public ssoToken!: string;
  @Prop({ type: Boolean, default: false }) public isOidcCallback!: boolean;

  @Action(SSO_LOGIN, StoreNamespace.AUTHENTICATION) public ssoLogin;
  @Action(OIDC_SSO_LOGIN, StoreNamespace.AUTHENTICATION) public oidcSsoLogin;
  @Action(RESET_STORE) public resetStore;

  @Mutation(SET_AUTH_LOADING_MODE, StoreNamespace.AUTHENTICATION) public setAuthLoadingMode;
  @Mutation(SET_SELECT_AUTH_METHOD_ERRORS, StoreNamespace.AUTHENTICATION) public setSelectAuthMethodErrors;
  @Mutation(SET_OIDC_ID, StoreNamespace.AUTHENTICATION) public setOidcId;
  @Mutation(SET_OIDC_USER_MANAGER, StoreNamespace.AUTHENTICATION) public setOidcUserManager;
  @Mutation(SET_OPEN_TERMS_AND_PRIVACY , StoreNamespace.SYSTEM) public setOpenTermsAndPrivacyModal;

  @Getter(OPEN_TERMS_AND_PRIVACY_MODAL, StoreNamespace.SYSTEM) public openTermsAndPrivacyModal!: boolean;
  @Getter(SELECT_AUTH_METHOD_ERRORS, StoreNamespace.AUTHENTICATION) public selectAuthMethodErrors!: string[];
  @Getter(LAST_SSO_USED_TOKEN, StoreNamespace.AUTHENTICATION) public lastSsoUsedToken!: string;
  @Getter(AUTH_LOADING_MODE, StoreNamespace.AUTHENTICATION) public loadingAuthMode!: boolean;
  @Getter(MUST_PERFORM_DATA_CLEANSING, StoreNamespace.LOBBY) public mustPerformDataCleansing!: boolean;
  @Getter(OIDC_ID, StoreNamespace.AUTHENTICATION) public oidcId!: string | null;
  @Getter(OIDC_USER_MANAGER, StoreNamespace.AUTHENTICATION) public oidcUserManager!: OidcUserManager;

  public created() {
    this.setSelectAuthMethodErrors([]);
  }

  public mounted() {
    if (this.isOidcCallback) {
      this.oidcCallbackLogin();
      return;
    }

    if (this.mustPerformDataCleansing) {
      return;
    }

    this.resetStore();

    if (!this.ssoToken || this.ssoToken === this.lastSsoUsedToken) {
      return;
    }

    this.setAuthLoadingMode(true);
    if (!!process.env.VUE_APP_RECAPTCHA_SITE_KEY) {
      this.$recaptchaLoaded().then(() => {
        this.$recaptcha(RecaptchaNamespace.LOGIN_VIA_SSO)
          .then(response => {
            this.setAuthLoadingMode(false);
            if (this.ssoToken !== this.lastSsoUsedToken) {
              this.ssoLogin({
                requestBody: {
                  ssoToken: this.ssoToken,
                  recaptchaResponse: response,
                },
              });
            }
          })
          .catch(() => {
            this.setAuthLoadingMode(false);
          });
      });
    } else {
      this.setAuthLoadingMode(false);
      this.ssoLogin({
        requestBody: {
          ssoToken: this.ssoToken,
        },
      });
    }
  }

  public navigateTo(route: string): void {
    route = this.gov ? route + '?c=gov' : route;
    this.$router.push({ path: route });
  }

  public closeTermsDialog(): void {
    this.setOpenTermsAndPrivacyModal(false);
  }

  public openTermsAndPrivacy(): void {
    this.setOpenTermsAndPrivacyModal(true);
  }

  public onCancel() {
    this.$router.push({ name: 'login' });
  }

  private async oidcCallbackLogin(): Promise<void> {
    if (this.mustPerformDataCleansing) {
      return;
    }

    let user: User | null = null;
    const oidcId = this.oidcId;
    const oidcUserManager = this.oidcUserManager;
    this.resetStore();
    this.setOidcId(oidcId);
    this.setOidcUserManager(oidcUserManager);
    this.setAuthLoadingMode(true);

    const userManager = OidcLogin.getUserManager(oidcUserManager);
    try {
      await userManager.signinCallback();
    } catch (e) {
      //do nothing
    }

    user = await userManager.getUser();
    if (user == null) {
      return;
    }

    if (!!process.env.VUE_APP_RECAPTCHA_SITE_KEY) {
      this.$recaptchaLoaded().then(() => {
        this.$recaptcha(RecaptchaNamespace.LOGIN_VIA_OIDC_SSO)
          .then(recaptchaResponse => {
            this.setAuthLoadingMode(false);
            this.doOidcLogin(recaptchaResponse, user as User, oidcId);
          })
          .catch(() => {
            this.setAuthLoadingMode(false);
          });
      });
    } else {
      this.setAuthLoadingMode(false);
      this.doOidcLogin(null, user as User, oidcId);
    }
  }

  private doOidcLogin(recaptchaResponse: string | null, user: User, oidcId: string | null): void {
    this.oidcSsoLogin({
      requestBody: {
        ssoAccessToken: user.access_token,
        ssoIdToken: user.id_token,
        oidcId,
        ...(recaptchaResponse && { recaptchaResponse }),
      } as OidcSsoRequest,
    });
  }
}

